import { buildApiUrl } from '../utils/apiConfig';
import { apiClient } from "../utils/apiClient";

/**
 * Per-school (Company) visibility flags for Polaris Athlete resources.
 * Controlled by admins/staff and consumed by the athlete UI to hide tabs/sections.
 */
export interface ResourceVisibility {
    collegeResourcesEnabled: boolean;
    recruitingResourcesEnabled: boolean;
    polarisHubVideosEnabled: boolean;
    polarisHubResourcesEnabled: boolean;
}

const defaultHeaders = { 'Content-Type': 'application/json' };
const defaultOptions: RequestInit = { credentials: 'include', headers: defaultHeaders };

/** Error carrying the HTTP status so callers can react to auth failures (401/403). */
export interface ResourceVisibilityError extends Error {
    status: number;
}

function toError(message: string, status: number): ResourceVisibilityError {
    const err = new Error(message) as ResourceVisibilityError;
    err.status = status;
    return err;
}

/**
 * Fetches the current user's school resource visibility flags. Works for admins,
 * staff, athletes and guardians since the company is resolved from the JWT/profile.
 */
export async function getResourceVisibility(): Promise<ResourceVisibility> {
    const res = await apiClient(buildApiUrl('api/company/resource-visibility'), defaultOptions);
    if (!res.ok) {
        throw toError(`Failed to fetch resource visibility: ${res.status}`, res.status);
    }
    return (await res.json()) as ResourceVisibility;
}

/**
 * Updates the school resource visibility flags. Restricted to admins/staff server-side.
 */
export async function updateResourceVisibility(
    payload: ResourceVisibility
): Promise<ResourceVisibility> {
    const res = await apiClient(buildApiUrl('api/company/resource-visibility'), {
        ...defaultOptions,
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        throw toError(`Failed to update resource visibility: ${res.status}`, res.status);
    }
    return (await res.json()) as ResourceVisibility;
}
